import { useFamily } from '@/hooks/useFamily';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AvatarWithNotification } from '@/components/ui/avatar-with-notification';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, Plus, Activity } from 'lucide-react';
import { useModalNavigation } from '@/hooks/useModalNavigation';
import { TodayTimeline } from './TodayTimeline';
import { SmartWeekView } from './SmartWeekView';
import { WorkspaceSelector } from '@/components/WorkspaceSelector';
import { startNewFamilyOnboarding } from '@/utils/onboardingUtils';

export function DesktopDashboard() {
  const { profile, family, familyMembers, createFamily } = useFamily();
  const { openModal } = useModalNavigation();

  return (
    <div className="container mx-auto px-6 py-8 space-y-8">
      {/* Workspace Selector */}
      <div className="mb-6">
        <WorkspaceSelector />
      </div>
      
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Avatar className="h-16 w-16">
            <AvatarImage src={profile?.avatar_url || ''} alt={profile?.first_name || ''} />
            <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xl">
              {profile?.first_name?.[0]}{profile?.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Bonjour {profile?.first_name} 👋
            </h1>
            <p className="text-muted-foreground text-lg">
              {family ? `Famille ${family.name}` : 'Créez votre première famille'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            onClick={() => openModal('createEvent')}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvel événement
          </Button>
          <Button
            onClick={() => openModal('createTask')}
            variant="outline"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle tâche
          </Button>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Today's focus */}
        <div className="lg:col-span-2 space-y-6">
          <TodayTimeline />
          <SmartWeekView />
        </div>

        {/* Right Column - Quick Info */}
        <div className="space-y-6">
          {/* Activity Summary Card */}
          <Card className="shadow-lg border-0 bg-gradient-to-br from-primary/10 to-primary/5">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold flex items-center gap-3 text-foreground">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <Activity className="h-5 w-5 text-primary-foreground" />
                </div>
                Activité récente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Vos dernières activités apparaîtront ici
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}